import { useState, useEffect, useCallback } from 'react'
import { Modal } from '@/shared/components/Modal'
import { adminApi } from '@/features/admin/api/admin'
import { getErrorMessage } from '@/shared/api/generated'
import type { PermissionJSON, UserResponse, ViewMemberResponse } from '@/shared/api/generated'

type PermKey = keyof PermissionJSON

const PERM_COLUMNS: { key: PermKey; label: string }[] = [
  { key: 'can_delete', label: 'Delete' },
  { key: 'can_upload', label: 'Upload' },
  { key: 'can_download', label: 'Download' },
  { key: 'can_list', label: 'List' },
  { key: 'can_rename', label: 'Rename' },
]

interface ManageViewDialogProps {
  groupId: number
  groupName: string
  onClose: () => void
  // Create mode: viewId=0, folderPath set
  // Manage mode: viewId>0
  viewId: number
  folderPath: string
  folderName: string
  onViewCreated?: () => void
  onViewDeleted?: () => void
}

export function ManageViewDialog({ groupId, groupName, onClose, viewId: initialViewId, folderPath, folderName, onViewCreated, onViewDeleted }: ManageViewDialogProps) {
  const [viewId, setViewId] = useState(initialViewId)
  const [viewName, setViewName] = useState(folderName)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')

  const isCreateMode = viewId === 0

  const handleCreate = async () => {
    if (!viewName.trim()) return
    setCreating(true)
    setError('')
    try {
      const view = await adminApi.createView(groupId, { name: viewName.trim(), base_path: folderPath, description: '' })
      setViewId(view.id)
      onViewCreated?.()
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm('Delete this view? Members will lose access.')) return
    setError('')
    try {
      await adminApi.deleteView(viewId)
      onViewDeleted?.()
      onClose()
    } catch (err) {
      setError(getErrorMessage(err))
    }
  }

  const title = isCreateMode ? `Create View in ${groupName}` : `Manage View: ${viewName}`

  return (
    <Modal title={title} onClose={onClose} wide>
      {error && <div className="alert error">{error}</div>}
      {isCreateMode ? (
        <div className="add-member-bar">
          <input type="text" value={viewName} onChange={(e) => setViewName(e.target.value)}
            placeholder="View name" autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreate() }} />
          <span className="muted">{folderPath}</span>
          <button className="btn-success" onClick={handleCreate} disabled={creating || !viewName.trim()}>
            {creating ? 'Creating...' : 'Create View'}
          </button>
        </div>
      ) : (
        <>
          <ViewMembersPanel viewId={viewId} />
          <div className="button-row" style={{ justifyContent: 'flex-end', paddingTop: 8 }}>
            <button className="btn-danger" onClick={handleDelete}>Delete View</button>
          </div>
        </>
      )}
    </Modal>
  )
}

function ViewMembersPanel({ viewId }: { viewId: number }) {
  const [members, setMembers] = useState<ViewMemberResponse[]>([])
  const [allUsers, setAllUsers] = useState<UserResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [staged, setStaged] = useState<Map<number, Partial<PermissionJSON>>>(new Map())
  const [removedIds, setRemovedIds] = useState<Set<number>>(new Set())
  const [addUsername, setAddUsername] = useState('')
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [vm, u] = await Promise.all([adminApi.listViewMembers(viewId), adminApi.listAllUsers()])
      setMembers(vm)
      setAllUsers(u)
      setStaged(new Map())
      setRemovedIds(new Set())
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [viewId])

  useEffect(() => { load() }, [load])

  const hasChanges = staged.size > 0 || removedIds.size > 0

  const getEffective = (m: ViewMemberResponse): PermissionJSON => {
    const s = staged.get(m.user_id)
    return {
      can_delete: s?.can_delete ?? m.can_delete,
      can_upload: s?.can_upload ?? m.can_upload,
      can_download: s?.can_download ?? m.can_download,
      can_list: s?.can_list ?? m.can_list,
      can_rename: s?.can_rename ?? m.can_rename, can_manage_vals: s?.can_manage_vals ?? m.can_manage_vals,
    }
  }

  const save = async () => {
    setSaving(true)
    setError('')
    try {
      for (const userId of removedIds) await adminApi.removeViewMember(viewId, userId)
      for (const [userId, changes] of staged) {
        if (removedIds.has(userId)) continue
        const member = members.find((m) => m.user_id === userId)!
        await adminApi.updateViewMemberPermissions(viewId, userId, {
          can_delete: changes.can_delete ?? member.can_delete,
          can_upload: changes.can_upload ?? member.can_upload,
          can_download: changes.can_download ?? member.can_download,
          can_list: changes.can_list ?? member.can_list,
          can_rename: changes.can_rename ?? member.can_rename, can_manage_vals: changes.can_manage_vals ?? member.can_manage_vals,
        })
      }
      await load()
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  const addMember = async () => {
    if (!addUsername) return
    setError('')
    try {
      await adminApi.addViewMember(viewId, addUsername, {
        can_delete: false, can_upload: false, can_download: true, can_list: true, can_rename: false, can_manage_vals: false,
      })
      setAddUsername('')
      await load()
    } catch (err) {
      setError(getErrorMessage(err))
    }
  }

  const memberIds = new Set(members.map((m) => m.user_id))
  const availableUsers = allUsers.filter((u) => !u.deleted && !memberIds.has(u.id))

  if (loading) return <div className="empty-state">Loading members...</div>

  return (
    <>
      {error && <div className="alert error">{error}</div>}
      {hasChanges && (
        <div className="button-row">
          <button className="btn-secondary" onClick={() => { setStaged(new Map()); setRemovedIds(new Set()) }} disabled={saving}>Discard</button>
          <button className="btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Username</th>
              {PERM_COLUMNS.map((col) => <th key={col.key} className="centered">{col.label}</th>)}
              <th className="right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.map((vm) => {
              const perms = getEffective(vm)
              const isRemoved = removedIds.has(vm.user_id)
              const cls = isRemoved ? 'removed' : staged.has(vm.user_id) ? 'changed' : undefined
              return (
                <tr key={vm.user_id} className={cls}>
                  <td>{vm.username}</td>
                  {PERM_COLUMNS.map((col) => (
                    <td key={col.key} className="centered">
                      <input type="checkbox" checked={perms[col.key]}
                        onChange={(e) => setStaged((prev) => {
                          const next = new Map(prev)
                          next.set(vm.user_id, { ...next.get(vm.user_id), [col.key]: e.target.checked })
                          return next
                        })}
                        disabled={isRemoved} />
                    </td>
                  ))}
                  <td className="right">
                    {isRemoved
                      ? <button className="link success" onClick={() => setRemovedIds((p) => { const n = new Set(p); n.delete(vm.user_id); return n })}>Undo</button>
                      : <button className="link danger" onClick={() => setRemovedIds((p) => new Set(p).add(vm.user_id))}>Remove</button>
                    }
                  </td>
                </tr>
              )
            })}
            {members.length === 0 && <tr><td colSpan={7} className="muted">No members yet</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="add-member-bar">
        <select value={addUsername} onChange={(e) => setAddUsername(e.target.value)}>
          <option value="">Select user...</option>
          {availableUsers.map((u) => <option key={u.id} value={u.username}>{u.username}</option>)}
        </select>
        <button className="btn-success" onClick={addMember} disabled={!addUsername}>Add Member</button>
      </div>
    </>
  )
}
